'use client';

import {
  forwardRef,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
  KeyboardEvent,
} from 'react';
import classnames from 'classnames';
import Box from '../../atoms/Box/Box';
import Typography from '../../atoms/Typography/Typography';
import styles from './Tabs.module.scss';

type Theme = 'dark' | 'light';

/**
 * Represents a single tab item configuration
 */
export type TabItem = {
  /** Unique identifier for the tab */
  id: string;
  /** Display label for the tab */
  label: string;
  /** Whether the tab is disabled */
  disabled?: boolean;
  /** Content to render when this tab is active */
  content?: ReactNode;
};

/**
 * Props for the Tabs component
 */
export type TabsProps = {
  /** Array of tab items to render */
  tabs: TabItem[];
  /** The currently active tab id (controlled mode) */
  activeTab?: string;
  /** Default active tab id (uncontrolled mode) */
  defaultActiveTab?: string;
  /** Callback when tab changes */
  onTabChange?: (tabId: string) => void;
  /** Visual theme @default 'light' */
  theme?: Theme;
  /** Test ID for testing */
  dataTestId: string;
  /** Additional class name */
  className?: string;
  /** Whether to render tab panels @default true */
  renderPanels?: boolean;
  /** Custom panel content (alternative to tab.content) */
  children?: ReactNode;
};

/**
 * Tabs component for organizing content into selectable views.
 * Supports both controlled and uncontrolled modes with full keyboard navigation.
 *
 * @component
 * @param {TabItem[]} props.tabs - Array of tab items to render
 * @param {string} [props.activeTab] - The currently active tab id (controlled mode)
 * @param {string} [props.defaultActiveTab] - Default active tab id (uncontrolled mode)
 * @param {function} [props.onTabChange] - Callback when tab changes
 * @param {'dark' | 'light'} [props.theme='light'] - Visual theme
 * @param {string} props.dataTestId - Test ID for testing
 * @param {string} [props.className] - Additional class name
 * @param {boolean} [props.renderPanels=true] - Whether to render tab panels
 * @param {ReactNode} [props.children] - Custom panel content (alternative to tab.content)
 * @returns {JSX.Element} The rendered Tabs component
 *
 * @example
 * ```tsx
 * <Tabs
 *   dataTestId="my-tabs"
 *   tabs={[
 *     { id: 'details', label: 'Details', content: <DetailsPanel /> },
 *     { id: 'documents', label: 'Documents', content: <DocumentsPanel /> },
 *   ]}
 *   onTabChange={(tabId) => console.log('Selected:', tabId)}
 * />
 * ```
 */
const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  (
    {
      tabs,
      activeTab: controlledActiveTab,
      defaultActiveTab,
      onTabChange,
      theme = 'dark',
      dataTestId,
      className,
      renderPanels = true,
      children,
    },
    ref
  ) => {
    const [internalActiveTab, setInternalActiveTab] = useState<string>(
      defaultActiveTab || tabs[0]?.id || ''
    );
    const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
    const textRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
    const indicatorRef = useRef<HTMLDivElement>(null);

    const isControlled = controlledActiveTab !== undefined;
    const activeTabId = isControlled ? controlledActiveTab : internalActiveTab;

    const updateIndicator = useCallback(() => {
      const activeTabElement = tabRefs.current.get(activeTabId);
      const activeTextElement = textRefs.current.get(activeTabId);
      const indicator = indicatorRef.current;

      if (activeTabElement && activeTextElement && indicator) {
        const textRect = activeTextElement.getBoundingClientRect();
        const tabListRect = activeTabElement.parentElement?.getBoundingClientRect();

        if (tabListRect) {
          const left = textRect.left - tabListRect.left;
          indicator.style.transform = `translateX(${left}px)`;
          indicator.style.width = `${textRect.width}px`;
        }
      }
    }, [activeTabId]);

    useEffect(() => {
      updateIndicator();
      window.addEventListener('resize', updateIndicator);
      return () => window.removeEventListener('resize', updateIndicator);
    }, [updateIndicator]);

    const handleTabClick = useCallback(
      (tabId: string, disabled?: boolean) => {
        if (disabled) return;

        if (!isControlled) {
          setInternalActiveTab(tabId);
        }
        onTabChange?.(tabId);
      },
      [isControlled, onTabChange]
    );

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
        const enabledTabs = tabs.filter((tab) => !tab.disabled);
        const currentEnabledIndex = enabledTabs.findIndex(
          (tab) => tab.id === tabs[currentIndex].id
        );

        let newIndex: number | null = null;

        switch (event.key) {
          case 'ArrowLeft':
            event.preventDefault();
            newIndex = currentEnabledIndex > 0 ? currentEnabledIndex - 1 : enabledTabs.length - 1;
            break;
          case 'ArrowRight':
            event.preventDefault();
            newIndex = currentEnabledIndex < enabledTabs.length - 1 ? currentEnabledIndex + 1 : 0;
            break;
          case 'Home':
            event.preventDefault();
            newIndex = 0;
            break;
          case 'End':
            event.preventDefault();
            newIndex = enabledTabs.length - 1;
            break;
          default:
            return;
        }

        if (newIndex !== null) {
          const newTab = enabledTabs[newIndex];
          tabRefs.current.get(newTab.id)?.focus();
          handleTabClick(newTab.id);
        }
      },
      [tabs, handleTabClick]
    );

    const setTabRef = useCallback(
      (tabId: string) => (el: HTMLButtonElement | null) => {
        if (el) {
          tabRefs.current.set(tabId, el);
        } else {
          tabRefs.current.delete(tabId);
        }
      },
      []
    );

    const setTextRef = useCallback(
      (tabId: string) => (el: HTMLSpanElement | null) => {
        if (el) {
          textRefs.current.set(tabId, el);
        } else {
          textRefs.current.delete(tabId);
        }
      },
      []
    );

    return (
      <Box
        ref={ref}
        className={classnames(styles.tabs, className)}
        data-theme={theme}
        testId={dataTestId}
      >
        <Box className={styles.tabList} role="tablist" aria-orientation="horizontal">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              ref={setTabRef(tab.id)}
              role="tab"
              type="button"
              id={`tab-${tab.id}`}
              aria-selected={activeTabId === tab.id}
              aria-controls={`panel-${tab.id}`}
              aria-disabled={tab.disabled}
              tabIndex={activeTabId === tab.id ? 0 : -1}
              className={classnames(styles.tab, {
                [styles.active]: activeTabId === tab.id,
                [styles.disabled]: tab.disabled,
              })}
              onClick={() => handleTabClick(tab.id, tab.disabled)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              data-testid={`${dataTestId}-tab-${tab.id}`}
            >
              <span ref={setTextRef(tab.id)} className={styles.tabText}>
                <Typography
                  variant="body"
                  size="medium"
                  theme={theme}
                  color={activeTabId === tab.id ? 'white' : 'gray-5'}
                >
                  {tab.label}
                </Typography>
              </span>
            </button>
          ))}
          <Box ref={indicatorRef} className={styles.indicator} aria-hidden="true" />
        </Box>

        {renderPanels && (
          <Box className={styles.panelContainer}>
            {children ||
              tabs.map((tab) => (
                <Box
                  key={tab.id}
                  id={`panel-${tab.id}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${tab.id}`}
                  hidden={activeTabId !== tab.id}
                  className={classnames(styles.panel, {
                    [styles.activePanel]: activeTabId === tab.id,
                  })}
                  testId={`${dataTestId}-panel-${tab.id}`}
                >
                  {activeTabId === tab.id && tab.content}
                </Box>
              ))}
          </Box>
        )}
      </Box>
    );
  }
);

Tabs.displayName = 'Tabs';

export default Tabs;
